/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useState,
	useMemo,
	useEffect,
	useRef,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import {
	getFullAssetURI,
	toMap,
	numberToPercent,
	percMultiply,
	getVideoRect,
	isVideo,
	safeIntFromPx,
} from '../../utils/utils';
import VideoButtons from './video-buttons';
import { GreenroomState as GS } from '../../stores/types/types';
import { fixedLayoutTypes } from '../../utils/video-layout';
import { ShotFeed } from '../../stores/reducers/shots';
import useShots from '../../contexts/shots/provider';
import CustomGraphicsCanvas from '../custom-graphics/custom-graphics-canvas';
import OverlayDropArea from '../custom-graphics/overlay-drop-area';
import { DebugGate } from '../debug-views/debug-panel/debug-gate';
import {
	DragItem, DRAG_TYPE, DropAreaData, DropCallbackData,
} from '../../stores/reducers/user-interface';
import AudioOnlyFeeds from './audio-only-feeds';
import { showAlert } from '../general-components/alert-service';
import { ALERT_TYPES } from '../general-components/alert-types';
import * as ShotsActions from '../../stores/actions/shots';
import { getShotStartTimestampEntity } from '../../connection/shots';
import PreloadVideo from '../../utils/preload-video';
import MainVideoView from './main-video-view';
import FeedMediaControls from './feed-media-controls';
import Spotlights from '../spotlight/spotlights';
import useCanvasConfig from '../../hooks/use-canvas-config';
import HoldSlide from './onsite-location/hold-slide';

const lowTone = require('../../audio/low-tone.mp3');

/*

WARNING: Our broadcasting application "bullman" loads the Greenroom with "embed" view on.
	Our broadcasting server doesn't have a logged in user. It essentially loads the main video player
	in fullscreen mode which is what is broadcasted. So don’t assume the ‘user’ variable
	from sessionReducer is going to be defined in any of the components that the embed
	view uses or else that would cause a bug in the broadcast.

Also, when you make changes to main-video-live and child components test
that the embed view is working as you would expect by passing the meeting_id into
https://greenroom.brandlive-dev.com/embed/[meeting_id]?debug=embed.
This is what the broadcasting server loads.

*/

interface OwnProps {
	socket: Socket | null;
}

export default function MainVideoLive(props: OwnProps) {
	const { socket } = props;
	const clearShot = useSelector((state: GS) => state.SettingsReducer.clearShot);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const connectedUsers = useSelector((state: GS) => state.SessionReducer.connectedUsers);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const streamState = useSelector((state: GS) => state.BroadcastReducer.streamState);
	const debugOverlaysEnabled = useSelector((state: GS) => state.DebugReducer.debugOverlaysEnabled);
	const globalIsDragging = useSelector((state: GS) => state.UserInterfaceReducer.globalIsDragging);
	const typeOfDragging = useSelector((state: GS) => state.UserInterfaceReducer.typeOfDragging);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const canvasStyle = useSelector((state: GS) => state.ShotsReducer.canvasStyle);

	const {
		backgroundColor,
		backgroundType, // image, video, or color. Video not yet implemented
		wallpaperColor, // color in hex
		wallpaperType,
		wallpaperScale,
		wallpaperImage,
		backgroundImage,
		backgroundScale, // a percentage to fill
		layoutSpacingPresets,
	} = canvasStyle;

	const {
		currentShot,
		nextShot,
		feedDroppedOnCanvas,
		overlayDroppedOnCanvas,
	} = useShots();

	const { shot_data: { shotGraphics } } = currentShot;
	const nextShotGraphics = nextShot?.shot_data?.shotGraphics ?? [];
	const {
		shotFeeds,
		forcedLayout,
		shouldGetOnsiteCanvasConfig,
		showingHoldSlide,
	} = useCanvasConfig(currentShot);

	const dispatch = useDispatch();
	const videoWrapperRef = useRef<HTMLDivElement | null>(null);
	const streamStateRef = useRef<number>(streamState);
	const [awaitingProducer, setAwaitingProducer] = useState(false);
	const [videoWidthHeight, setVideoWidthHeight] = useState({
		height: '0', width: '0', top: '0px', left: '0px', bottom: '0px', right: '0px',
	});
	const standardFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);
	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);
	const [showAudioOnlyFeedsButton, setShowAudioOnlyFeedsButton] = useState(false);

	useEffect(() => { streamStateRef.current = streamState; }, [streamState]);
	useEffect(() => {
		if (embed && token) {
			dispatch(BroadcastActions.getDatabaseStreamState(token, meeting!.meeting));
		}
	}, []);

	useEffect(() => {
		setShowAudioOnlyFeedsButton(host || audioOnlyFeeds.length > 0);
	}, [host, audioOnlyFeeds]);

	const sendEmbedStateUpdate = (currentStreamState: number) => {
		socket?.sendMessage('embed-state-update', {
			newStreamState: currentStreamState,
		});
	};

	useEffect(() => {
		// Call beehive for shotTimeStartEntity

		const requestShotTimeStartEntity = async () => {
			if (token && meeting) {
				const shotTimestamp = await getShotStartTimestampEntity(token, meeting?.meeting);
				if (!shotTimestamp) return;
				dispatch(ShotsActions.setPlayingShot(shotTimestamp));
			}
		};

		requestShotTimeStartEntity();

		// listener for requesting the pre-show/post-show media timestamp from embed app
		if (host) socket?.sendMessage('request-embed-state', {});

		socket?.sendMessage('request-media-video-state', {});

		return () => {
			socket?.removeListener('request-playing-shot-state');
		};
	}, []);

	useEffect(() => {
		// these need refs to fire
		if (embed) { socket?.addListener('request-embed-state', () => sendEmbedStateUpdate(streamState)); }

		return () => {
			if (embed) { socket?.removeListener('request-embed-state'); }
		};
	}, [streamState, socket]);

	const backgroundStyle: any = useMemo(() => {
		const newStyle: any = {};
		newStyle.backgroundColor = backgroundColor;
		newStyle.display = 'grid';
		return newStyle;
	}, [backgroundColor, backgroundImage, backgroundScale, backgroundType]);

	const wallpaperStyle: any = useMemo(() => {
		const newStyle: any = {};

		newStyle.backgroundColor = wallpaperColor;
		if (wallpaperType === 'image') {
			newStyle.backgroundSize = `${wallpaperScale}%`;
			newStyle.backgroundImage = `url('${getFullAssetURI(wallpaperImage)}')`;
		}

		return newStyle;
	}, [wallpaperColor, wallpaperScale, wallpaperImage, wallpaperType]);

	function margins() {
		// We created some media specific layouts that have "media" in their type title.
		// These layouts have been calculated for 16:9 and 4:3 media layouts with specific
		// presenter sizes and gaps as well.
		// When these are selected we ignore spacing settings (borders still work though).
		const isFixedLayout = forcedLayout
			? fixedLayoutTypes.some((layout) => forcedLayout.includes(layout))
			: false;

		if (isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
			};
		}
		const layoutMap = toMap('layout', layoutSpacingPresets);
		// if no layout exists set no margins
		const layout = layoutMap.get(forcedLayout);
		const backup = {
			spacing: {
				top: 0, bottom: 0, side: 0, inner: 0,
			},
		};
		const marginSpacing = layout?.spacing ? layout : backup;
		const {
			inner, top, side, bottom,
		} = marginSpacing.spacing;
		return {
			columnGap: numberToPercent(inner),
			rowGap: percMultiply(numberToPercent(inner) || '0%'),
			paddingTop: numberToPercent(top),
			paddingBottom: numberToPercent(bottom),
			paddingRight: numberToPercent(side),
			paddingLeft: numberToPercent(side),
		};
	}

	const marginMemo = useMemo(() => margins(), [forcedLayout, shotFeeds, layoutSpacingPresets]);

	useEffect(() => {
		if (!awaitingProducer) return;
		if (connectedUsers.find((u: any) => u.isProducer)) setAwaitingProducer(false);
	}, [connectedUsers]);

	const numberOfVideosOnCanvas = () => {
		const vidNumber = currentShot.shot_data.shotFeeds.reduce(
			(previousValue:number, currentValue:ShotFeed) => {
				const singleMedia = media.find((elm) => elm.id === currentValue.media);
				let videoCheck = false;
				if (singleMedia) {
					videoCheck = isVideo(singleMedia.type);
				}
				return previousValue + (videoCheck ? 1 : 0);
			},
			0,
		);
		return vidNumber;
	};

	// we resolve drops in this component because it's the common ancestor
	// of the feed components, so we can pass down the function to call on drop
	const feedDrop = (item: DragItem, dropAreaData?: DropAreaData) => {
		const mediaBeingReplacedId = shotFeeds.find(
			(shot) => shot.position === dropAreaData?.feedPosition,
		)?.media;
		const mediaItemBeingReplaced = media.find((i) => i.id === mediaBeingReplacedId);
		const replacingItem = media.find((mediaItem) => mediaItem.id === item.id);
		const beingReplacedIsAVideo = isVideo(mediaItemBeingReplaced?.type || '');
		const replacingItemIsVideo = isVideo(replacingItem?.type || '');
		const videoIsInCanvas = shotFeeds.some(
			(shot) => shot.media === replacingItem?.id,
		);
		const canDropItem = replacingItemIsVideo
			? (numberOfVideosOnCanvas() < 8)
				|| (numberOfVideosOnCanvas() === 8 && beingReplacedIsAVideo) || videoIsInCanvas
			: true;
		if (typeof dropAreaData?.feedPosition === 'number' && canDropItem) {
			feedDroppedOnCanvas(item, socket, false, dropAreaData.feedPosition);
		} else {
			showAlert({
				type: ALERT_TYPES.WARNING,
				message: 'Video Capacity Reached',
				description: 'Only 8 videos are allowed on the canvas at a given time',
				duration: 5000,
			});
		}
	};

	const overlayDrop = (item: DragItem, dropData: DropCallbackData) => {
		overlayDroppedOnCanvas(item, socket, false, dropData);
	};

	const resizeObserver = new ResizeObserver((entries) => {
		// 177%, 56.25%
		const wrapWidth = entries[0].contentRect.width;
		const wrapHeight = entries[0].contentRect.height;

		const styleObject = {
			...getVideoRect(wrapWidth, wrapHeight),
			right: 'auto',
			bottom: 'auto',
		};

		setVideoWidthHeight(styleObject);
	});

	useEffect(() => {
		if (videoWrapperRef.current) { resizeObserver.observe(videoWrapperRef.current); }

		return () => {
			if (videoWrapperRef.current) resizeObserver.disconnect();
		};
	}, [videoWrapperRef]);

	const combinedGraphicIds = useMemo(() => {
		const nShotGraphics = nextShot?.shot_data.shotGraphics ?? [];
		return Array.from(new Set([...(shotGraphics ?? []), ...nShotGraphics]));
	}, [shotGraphics, nextShot?.shot_data.shotGraphics])

	/*
		Extract here to avoid unwanted unmount,
		but probably needs a better approach
	*/

	const graphicsCanvas = useMemo((): JSX.Element => (
		<CustomGraphicsCanvas
			renderedGraphics={combinedGraphicIds}
			currentShotGraphics={shotGraphics}
			canEdit={false}
			height={safeIntFromPx(videoWidthHeight.height)}
			width={safeIntFromPx(videoWidthHeight.width)}
		/>
	), [combinedGraphicIds, shotGraphics, videoWidthHeight.height, videoWidthHeight.width]);

	return (
		<>
			{embed &&
				<PreloadVideo />
			}
			{embed && <audio
				src={lowTone}
				autoPlay={true}
				loop={true}
				onPlaying={(e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
					(e.target as HTMLAudioElement).volume = 0.05;
				}}
			></audio>}
			<Spotlights/>
			<div className={classNames('left-panel-content', { embed })}>
				<div id="video-wrapper" ref={videoWrapperRef} className={classNames({ embed, 'include-audio-only-feeds': !embed })}>
					{shotFeeds && shotFeeds.length > 0 && !clearShot
						? (
							<div
								style={{
									...backgroundStyle,
									...videoWidthHeight,
								}}
								className={classNames(
									'main-video-container',
									{ 'is-producer': host },
									{ 'rounded-border': !host && !embed, embed },
								)}>
								{
									videoWidthHeight
									&& <DebugGate debugFeatureFlag={debugOverlaysEnabled}>
										{graphicsCanvas}
									</DebugGate>
								}
								<MainVideoView
									key={'main-video-view-2'}
									id={'main-video-view-next'}
									dropCallback={undefined}
									selectedShotFeeds={[]}
									audioOnlyFeeds={[]}
									forHidden={true}
								/>
									<div key='curtain-container'className='curtain-container' style={{ backgroundColor }}>
											{(backgroundType === 'image') && <img key='curtain' id='curtain' src={getFullAssetURI(backgroundImage)} style={{
												width: `${backgroundScale}%`,
												margin: 'auto',
											}}></img>}
									</div>

								<MainVideoView
									key={'main-video-view-1'}
									id={'main-video-view-current'}
									selectedShotFeeds={standardFeeds}
									audioOnlyFeeds={audioOnlyFeeds}
									dropCallback={feedDrop}/>
									<FeedMediaControls ></FeedMediaControls>
								<div
									id="controls-container-live"
									data-testid="controls-container-live"
									className={classNames('main-video', forcedLayout)}
									style={{ ...marginMemo, pointerEvents: globalIsDragging ? 'none' : 'auto' }}
								/>
								{typeOfDragging === DRAG_TYPE.OVERLAY
									&& <OverlayDropArea dropCallback={overlayDrop} />
								}
								{!embed && (
									<div className={classNames('button-row-below-video', showAudioOnlyFeedsButton && 'show-audio-only-button')}>
										{showAudioOnlyFeedsButton && (
											<AudioOnlyFeeds isForShotEditorModal={false} socket={socket} />
										)}
										<div className="snackbar-container" id="portal-unmute-snackbar-1" />
									</div>
								)}
								{shouldGetOnsiteCanvasConfig && (
									<HoldSlide showHoldSlide={showingHoldSlide} />
								)}
							</div>
						) : (
							<div style={{ ...wallpaperStyle, ...videoWidthHeight }} className={classNames(
								`main-video-container ${host || embed ? '' : 'rounded-border'}`,
							)}>
								{!embed && awaitingProducer
									&& <div className="without-producer-container rounded-border">
										<div className="without-producer">
											<span>
												Waiting for Producer
											</span>
										</div>
									</div>
								}
							</div>
						)
					}
				</div>

				{!embed && (
					<div
						className="left-panel-below-video">
						<VideoButtons
							socket={socket}
							shotBeingEdited={currentShot}
							isForShotEditorModal={false}
							// treated as 0th indexed feed
							feedPosition={0}
							dropCallback={feedDrop}
						/>
					</div>
				)}
			</div >
		</>
	);
}
